import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Import translation
import { API_BASE } from '../config';
import Markdown from './common/Markdown';
import CoachSummaryCard from './WorkoutDebrief/CoachSummaryCard';
import CoachDeepDive from './WorkoutDebrief/CoachDeepDive';
import CoachRecommendations from './WorkoutDebrief/CoachRecommendations';
import CoachOptions from './WorkoutDebrief/CoachOptions';
import AthleteFeedbackEditor from './WorkoutDebrief/AthleteFeedbackEditor';
import ExecutedHrZonesChart from './WorkoutDebrief/ExecutedHrZonesChart';
import ExecutedSplitsOrLapsTable from './WorkoutDebrief/ExecutedSplitsOrLapsTable';
import StravaFormatCard from './WorkoutDebrief/StravaFormatCard';
import { HiArrowLeft, HiEllipsisHorizontal, HiTrash, HiCheck, HiCloudArrowUp } from 'react-icons/hi2';
import { FaThumbsUp, FaThumbsDown } from 'react-icons/fa';
import MobileBottomTabs from './Plans/MobileBottomTabs';

const WorkoutDetails = () => {
    const { t, i18n } = useTranslation();
    const { workoutId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [workout, setWorkout] = useState(null);
    const [loading, setLoading] = useState(true);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [debrief, setDebrief] = useState(null);
    const [canEdit, setCanEdit] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteInput, setDeleteInput] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [showMenu, setShowMenu] = useState(false);

    const [applyingMod, setApplyingMod] = useState(false);
    const [dismissingMod, setDismissingMod] = useState(false);
    const [rerunLoading, setRerunLoading] = useState(false);
    const [coachRatingSaving, setCoachRatingSaving] = useState(false);
    const [feedbackEditing, setFeedbackEditing] = useState(false);
    const [reanalyzing, setReanalyzing] = useState(false);
    const [justReanalyzed, setJustReanalyzed] = useState(false);
    const autoOpenedFeedbackRef = useRef(false);

    const menuRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const loadWorkout = useCallback(() => {
        return fetch(`${API_BASE}/workouts/${workoutId}`, { credentials: 'include' })
            .then(res => res.json())
            .then(data => {
                setWorkout(data);
                if (data.debrief_data) {
                    setDebrief(data.debrief_data);
                }
                setCanEdit(data.can_edit !== false);
                setLoading(false);
            })
            .catch(err => console.error(err));
    }, [workoutId]);

    useEffect(() => {
        loadWorkout();
    }, [loadWorkout]);

    // ?feedback=1 (e.g. from a notification): open the feedback editor and scroll to it.
    useEffect(() => {
        if (autoOpenedFeedbackRef.current) return;
        if (!workout || workout.status !== 'completed' || !canEdit) return;
        const params = new URLSearchParams(location.search);
        if (params.get('feedback') !== '1') return;
        autoOpenedFeedbackRef.current = true;
        setFeedbackEditing(true);
    }, [workout, canEdit, location.search]);

    useEffect(() => {
        if (!feedbackEditing) return;
        requestAnimationFrame(() => {
            const el = document.getElementById('athlete-feedback');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }, [feedbackEditing]);

    const handleApplyModifications = async () => {
        const effectiveMods =
            (debrief?.coach_modifications) ||
            (debrief?.coach_analysis?.recommendations?.adjust_plan?.modifications) ||
            null;
        if (!effectiveMods || Object.keys(effectiveMods).length === 0) return;
        setApplyingMod(true);

        try {
            // We reuse the existing endpoint for applying modifications
            // We need to format the payload to match what apply-modification expects
            // It expects: { modifications: {}, request_text: string, analysis: object }

            const payload = {
                modifications: effectiveMods,
                request_text: "Coach Adaptation after workout execution",
                analysis: debrief?.coach_analysis
                    ? debrief.coach_analysis
                    : {
                        analysis: debrief?.coach_feedback,
                        suggestions: [] // Legacy shape; kept for backward compatibility
                },
                source_workout_id: workoutId
            };

            const res = await fetch(`${API_BASE}/plans/${workout.plan_id}/apply-workout-patches`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            if (!res.ok) throw new Error("Failed to apply modifications");

            alert("Coach recommendations have been applied to your plan.");
            window.location.href = `/plans/${workout.plan_id}`;

        } catch (err) {
            console.error(err);
            alert("Error applying modifications: " + err.message);
        } finally {
            setApplyingMod(false);
        }
    };

    const handleDismissModifications = async () => {
        setDismissingMod(true);
        try {
            const res = await fetch(`${API_BASE}/plans/${workout.plan_id}/dismiss-modification`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ source_workout_id: workoutId })
            });

            if (!res.ok) throw new Error("Failed to dismiss");

            // Update local state to hide buttons
            setDebrief(prev => ({
                ...prev,
                modifications_dismissed: true
            }));

        } catch (err) {
            console.error(err);
            alert("Error dismissing: " + err.message);
        } finally {
            setDismissingMod(false);
        }
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);

        const formData = new FormData();
        formData.append('gpx', file);

        try {
            const res = await fetch(`${API_BASE}/workouts/${workoutId}/complete`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            const data = await res.json();
            if (data.debrief) {
                setDebrief(data.debrief);
                // Reload workout to get status update
                setWorkout(prev => ({ ...prev, status: 'completed' }));
            }
        } catch (err) {
            alert("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleRerunAnalysis = async () => {
        setRerunLoading(true);
        try {
            const res = await fetch(`${API_BASE}/workouts/${workoutId}/rerun-analysis`, {
                method: 'POST',
                credentials: 'include'
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.error || 'Failed to rerun analysis');
            if (data.debrief) setDebrief(data.debrief);
        } catch (err) {
            console.error(err);
            alert(t('workout.rerun_analysis_error'));
        } finally {
            setRerunLoading(false);
        }
    };

    const handleDelete = async () => {
        const confirmWord = t('workout.delete_instruction').includes('SUPPRIMER') ? 'SUPPRIMER' : 'DELETE';
        if (deleteInput !== confirmWord) {
            return;
        }

        setDeleting(true);
        try {
            const res = await fetch(`${API_BASE}/workouts/${workoutId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!res.ok) throw new Error("Failed");

            // Redirect back to plan
            navigate(`/plans/${workout.plan_id}`);
        } catch (err) {
            alert(t('workout.delete_error'));
            setDeleting(false);
        }
    };

    const handleMobileTabChange = (tab) => {
        if (!workout?.plan_id) return;
        if (tab === 'calendar') {
            navigate(`/plans/${workout.plan_id}?tab=calendar`);
        } else if (tab === 'chat') {
            navigate(`/plans/${workout.plan_id}?tab=chat`);
        } else {
            navigate(`/plans/${workout.plan_id}`);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;
    if (!workout) return <div className="p-8 text-center">Workout not found</div>;

    const workout_json = workout?.workout_json || {};
    const coachAnalysis = debrief?.coach_analysis || null;
    const coachModifications = debrief?.coach_modifications || coachAnalysis?.recommendations?.adjust_plan?.modifications || null;
    const isLegacyCoachDebrief = Boolean(debrief && !coachAnalysis && debrief.coach_feedback);
    const isCoachV2 = coachAnalysis?.version === 2;
    const reanalyzedAt = debrief?.reanalyzed_at ? new Date(debrief.reanalyzed_at) : null;
    const coachRating = debrief?.coach_rating || null; // 'up' | 'down' | null
    const executedSummary = debrief?.gpx_summary || {
      distance_km: 0,
      duration_seconds: 0,
      elevation_gain_m: 0,
      avg_hr: null
    };

    return (
        <div className="max-w-4xl mx-auto p-4 md:p-8 bg-white shadow-2xl rounded-3xl mt-0 md:mt-10 relative mb-10 ring-1 ring-gray-900/5 pb-24 md:pb-8">
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
               <Link 
                   to={`/plans/${workout.plan_id}?tab=calendar`} 
                   className="inline-flex items-center gap-2 text-gray-500 hover:text-indigo-600 font-bold transition-colors py-2"
               >
                   <HiArrowLeft className="w-5 h-5" /> {t('nav.back')}
               </Link>
               
               {/* Options Menu */}
               {canEdit && workout.status === 'completed' && (
                   <div className="relative" ref={menuRef}>
                       <button
                           onClick={() => setShowMenu(!showMenu)}
                           className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                       >
                           <HiEllipsisHorizontal className="w-7 h-7" />
                       </button>

                       {showMenu && (
                           <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-20 animate-in fade-in zoom-in-95">
                               <button
                                   onClick={() => { setShowMenu(false); setShowDeleteModal(true); }}
                                   className="w-full text-left px-4 py-3 text-sm font-bold text-red-600 hover:bg-red-50 flex items-center gap-2"
                               >
                                   <HiTrash className="w-5 h-5" />
                                   {t('workout.delete')}
                               </button>
                           </div>
                       )}
                   </div>
               )}
            </div>

            {/* Delete Modal */}
            {showDeleteModal && canEdit && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl">
                        <div className="flex justify-center mb-4 text-red-100 bg-red-50 w-16 h-16 rounded-full items-center mx-auto">
                            <HiTrash className="w-8 h-8 text-red-500" />
                        </div>
                        <h3 className="text-xl font-black text-center text-gray-900 mb-2">{t('workout.delete_confirm_title')}</h3>
                        <p className="text-gray-500 text-center mb-6">{t('workout.delete_confirm_msg')}</p>

                        <label className="block text-xs font-bold uppercase text-gray-400 mb-2 tracking-wide">{t('workout.delete_instruction')}</label>
                        <input
                            type="text"
                            className="w-full border-2 border-gray-200 p-3 rounded-xl mb-6 uppercase focus:ring-2 focus:ring-red-500/20 focus:border-red-500 outline-none font-bold"
                            value={deleteInput}
                            onChange={(e) => setDeleteInput(e.target.value.toUpperCase())}
                            placeholder={t('workout.delete_instruction').includes('SUPPRIMER') ? 'SUPPRIMER' : 'DELETE'}
                        />

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => { setShowDeleteModal(false); setDeleteInput(''); }}
                                className="px-5 py-3 text-gray-600 font-bold hover:bg-gray-100 rounded-xl transition-colors"
                            >
                                {t('workout.cancel')}
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleteInput !== (t('workout.delete_instruction').includes('SUPPRIMER') ? 'SUPPRIMER' : 'DELETE') || deleting}
                                className={`px-5 py-3 rounded-xl text-white font-bold shadow-lg transition-all ${
                                    deleteInput === (t('workout.delete_instruction').includes('SUPPRIMER') ? 'SUPPRIMER' : 'DELETE')
                                    ? 'bg-red-600 hover:bg-red-700 hover:shadow-red-500/30'
                                    : 'bg-red-300 cursor-not-allowed'
                                }`}
                            >
                                {deleting ? '...' : t('workout.delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Title Block */}
            <div className="mb-8">
                <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-2 tracking-tight leading-tight">
                    {workout_json.title || t('workout.planned_session')}
                </h1>
                <div className="flex items-center gap-2 text-gray-500 font-medium">
                    <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg text-sm font-bold">
                        {new Date(workout.scheduled_date).toDateString()}
                    </span>
                </div>
            </div>

            {/* Planned Session Card */}
            <div className="bg-white p-6 md:p-8 rounded-3xl mb-10 border border-gray-100 shadow-xl shadow-indigo-100/50">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-1.5 h-8 bg-indigo-500 rounded-full"></div>
                    <h3 className="font-black text-xl text-gray-900 tracking-tight">{t('workout.planned_session')}</h3>
                </div>
                
                <Markdown className="text-gray-700 text-base md:text-lg leading-relaxed [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 font-serif">
                  {workout_json.description || '—'}
                </Markdown>
                
                <div className="mt-6 flex flex-wrap gap-3">
                    {Number(workout_json.distance_km) > 0 && (
                        <span className="bg-indigo-50 text-indigo-700 border border-indigo-100 px-4 py-2 rounded-xl text-sm font-black tabular-nums">
                            {workout_json.distance_km} km
                        </span>
                    )}
                    {Number(workout_json.duration_min) > 0 && (
                        <span className="bg-indigo-50 text-indigo-700 border border-indigo-100 px-4 py-2 rounded-xl text-sm font-black tabular-nums">
                            {workout_json.duration_min} min
                        </span>
                    )}
                </div>
            </div>

            {/* Executed Session Section */}
            <div className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-1.5 h-8 bg-green-500 rounded-full"></div>
                    <h3 className="font-black text-xl text-gray-900 tracking-tight">{t('workout.executed_session')}</h3>
                </div>
                
                {/* Modern Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 text-center">
                        <div className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-1">{t('workout.distance')}</div>
                        <div className="font-black text-2xl text-gray-900 tabular-nums">{Number(executedSummary.distance_km || 0)} <span className="text-sm text-gray-400 font-bold">km</span></div>
                    </div>
                    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 text-center">
                        <div className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-1">{t('workout.duration')}</div>
                        <div className="font-black text-2xl text-gray-900 tabular-nums">{Math.round(Number(executedSummary.duration_seconds || 0) / 60)} <span className="text-sm text-gray-400 font-bold">min</span></div>
                    </div>
                    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 text-center">
                        <div className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-1">{t('workout.avg_hr')}</div>
                        <div className="font-black text-2xl text-gray-900 tabular-nums">
                            {executedSummary.avg_hr != null ? executedSummary.avg_hr : '—'} <span className="text-sm text-gray-400 font-bold">bpm</span>
                        </div>
                    </div>
                    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 text-center">
                        <div className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-1">{t('workout.elevation')}</div>
                        <div className="font-black text-2xl text-gray-900 tabular-nums">+{Number(executedSummary.elevation_gain_m || 0)} <span className="text-sm text-gray-400 font-bold">m</span></div>
                    </div>
                </div>

                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden mb-8">
                    <div className="p-1">
                        <ExecutedHrZonesChart hrZones={debrief?.hr_zones || null} />
                    </div>
                </div>
                
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden mb-8">
                     <ExecutedSplitsOrLapsTable debrief={debrief} />
                </div>

                {/* Athlete feedback ("Ton ressenti"): display + edit + optional re-run */}
                {workout.status === 'completed' && debrief ? (
                    <AthleteFeedbackEditor
                        workoutId={workoutId}
                        debrief={debrief}
                        canEdit={canEdit}
                        isEditing={feedbackEditing}
                        onEditingChange={setFeedbackEditing}
                        onRerunStart={() => { setReanalyzing(true); setJustReanalyzed(false); }}
                        onRerunEnd={() => setReanalyzing(false)}
                        onSaved={(nextDebrief, { rerun, reanalyzed }) => {
                            if (nextDebrief) setDebrief(nextDebrief);
                            if (rerun && reanalyzed) setJustReanalyzed(true);
                        }}
                    />
                ) : debrief?.user_feedback ? (
                    <div className="bg-gray-50/80 p-6 rounded-3xl border border-gray-100">
                         <h4 className="font-black text-gray-400 text-xs uppercase tracking-widest mb-3">{t('workout.user_feedback')}</h4>
                         <p className="text-gray-700 italic text-lg leading-relaxed">"{debrief.user_feedback}"</p>
                    </div>
                ) : null}
            </div>

            {debrief ? (
                <div className="animate-fade-in">
                    <div className="flex flex-wrap items-center gap-3 mb-6">
                        <div className="w-1.5 h-8 bg-amber-500 rounded-full"></div>
                        <h2 className="text-2xl font-black text-gray-900 tracking-tight">{t('workout.coach_debrief')}</h2>
                        {reanalyzedAt && !Number.isNaN(reanalyzedAt.getTime()) ? (
                            <span className={`px-3 py-1 rounded-full text-[11px] font-black border ${justReanalyzed ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                                {t('feedback.reanalyzed_at', { date: reanalyzedAt.toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }) })}
                            </span>
                        ) : null}
                    </div>

                    {reanalyzing ? (
                        <div className="mb-6 rounded-2xl border border-indigo-100 bg-indigo-50 p-4 flex items-center gap-3">
                            <span className="animate-spin h-5 w-5 border-2 border-indigo-600 border-t-transparent rounded-full shrink-0" />
                            <div className="text-sm font-black text-indigo-900">{t('feedback.rerun_loading')}</div>
                        </div>
                    ) : null}

                    {isLegacyCoachDebrief ? (
                      <div className="mb-6 bg-amber-50 border border-amber-200 rounded-3xl p-6 flex items-start justify-between gap-6">
                        <div>
                          <div className="text-base font-black text-amber-900 mb-1">{t('workout.rerun_analysis_title')}</div>
                          <div className="text-sm text-amber-800 leading-relaxed">{t('workout.rerun_analysis_hint')}</div>
                        </div>
                        <button
                          type="button"
                          onClick={handleRerunAnalysis}
                          disabled={rerunLoading}
                          className={`px-5 py-3 rounded-full font-bold text-white shadow-lg transition-all ${
                            rerunLoading ? 'bg-amber-300 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700 hover:shadow-amber-500/30 hover:-translate-y-0.5'
                          }`}
                        >
                          {rerunLoading ? t('workout.rerun_analysis_running') : t('workout.rerun_analysis')}
                        </button>
                      </div>
                    ) : null}

                    {/* Coach Debrief (Structured) */}
                    {coachAnalysis && isCoachV2 ? (
                      <div className={`space-y-8 transition-opacity ${reanalyzing ? 'opacity-50' : ''}`}>
                        <CoachSummaryCard coachAnalysis={coachAnalysis} />
                        <CoachOptions
                          key={`${workoutId}-${debrief?.reanalyzed_at || debrief?.analysis_date || ''}`}
                          analysis={coachAnalysis}
                          decision={debrief?.option_decision || null}
                          planId={workout.plan_id}
                          source="workout"
                          sourceId={workoutId}
                          canEdit={canEdit}
                          onApplied={() => { loadWorkout(); }}
                          onAnswerQuestion={workout.status === 'completed' ? () => setFeedbackEditing(true) : null}
                        />
                        <CoachDeepDive coachAnalysis={coachAnalysis} recentContext={debrief?.recent_context || null} />
                        <CoachRecommendations coachAnalysis={coachAnalysis} modifications={null} canEdit={false} />
                        {canEdit && debrief?.option_decision?.action === 'applied' ? (
                          <Link
                            to={`/plans/${workout.plan_id}?tab=calendar`}
                            className="inline-flex items-center gap-2 text-sm font-bold text-indigo-600 hover:text-indigo-800"
                          >
                            {t('coach_options.view_plan')} →
                          </Link>
                        ) : null}
                      </div>
                    ) : coachAnalysis ? (
                      <div className="space-y-8">
                        <CoachSummaryCard coachAnalysis={coachAnalysis} />
                        <CoachDeepDive coachAnalysis={coachAnalysis} recentContext={debrief?.recent_context || null} />
                        <CoachRecommendations
                          coachAnalysis={coachAnalysis}
                          modifications={coachModifications}
                          canEdit={canEdit}
                          modificationsApplied={Boolean(debrief.modifications_applied)}
                          modificationsDismissed={Boolean(debrief.modifications_dismissed)}
                          applyingMod={applyingMod}
                          dismissingMod={dismissingMod}
                          onApply={handleApplyModifications}
                          onDismiss={handleDismissModifications}
                        />
                      </div>
                    ) : debrief?.coach_feedback ? (
                      <div className="bg-indigo-50 border-l-4 border-indigo-500 p-6 mb-8 rounded-r-2xl">
                          <div className="font-black text-indigo-900 mb-3 text-lg flex items-center gap-2">
                              <span>🤖</span> {t('workout.coach_analysis')}
                          </div>
                          <Markdown className="text-indigo-800 text-base leading-relaxed [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1">
                            {debrief.coach_feedback}
                          </Markdown>
                      </div>
                    ) : null}

                    {/* Coach modifications are now rendered inside CoachRecommendations when structured analysis is available. */}
                    {!coachAnalysis && coachModifications && Object.keys(coachModifications).length > 0 ? (
                        <div className="bg-yellow-50 border border-yellow-200 p-8 rounded-3xl shadow-sm my-8">
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <h4 className="font-black text-yellow-800 text-xl mb-2">⚠️ {t('workout.suggested_adaptations')}</h4>
                                    <p className="text-yellow-700">{t('workout.adaptations_intro')}</p>
                                </div>
                            </div>

                            <div className="space-y-4 mb-8">
                          {Object.entries(coachModifications).map(([key, mod], index) => (
                                    <div key={index} className="bg-white p-6 rounded-2xl border border-yellow-100 shadow-sm">
                                        <div className="flex justify-between items-center mb-3 border-b border-gray-100 pb-3">
                                <span className="font-black text-gray-800 text-lg">{key}</span>
                                            <div className="flex gap-2 text-xs font-black uppercase text-gray-400">
                                                {mod.distance_km && <span className="bg-gray-100 px-2 py-1 rounded-md">{mod.distance_km} km</span>}
                                                {mod.duration_min && <span className="bg-gray-100 px-2 py-1 rounded-md">{mod.duration_min} min</span>}
                                            </div>
                                        </div>
                                        <p className="text-gray-600 leading-relaxed">{mod.description}</p>
                                    </div>
                                ))}
                            </div>

                            {debrief.modifications_applied ? (
                                <div className="w-full py-4 rounded-xl font-bold bg-green-100 text-green-800 text-center border border-green-200 flex items-center justify-center gap-2">
                                    <HiCheck className="w-5 h-5" /> {t('workout.recommendations_applied')}
                                </div>
                            ) : debrief.modifications_dismissed ? (
                                <div className="w-full py-4 rounded-xl font-bold bg-gray-100 text-gray-600 text-center border border-gray-200">
                                    <span>✖️</span> {t('workout.recommendations_dismissed')}
                                </div>
                            ) : !canEdit ? (
                                <div className="w-full py-4 rounded-xl font-bold bg-gray-100 text-gray-700 text-center border border-gray-200">
                            {t('workout.read_only')}
                                </div>
                            ) : (
                                <div className="flex flex-col gap-3">
                                    <button
                                        onClick={handleApplyModifications}
                                        disabled={applyingMod || dismissingMod}
                                        className={`w-full py-4 rounded-2xl font-bold text-white transition shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2 text-lg ${
                                            applyingMod ? 'bg-yellow-400 cursor-not-allowed' : 'bg-yellow-600 hover:bg-yellow-700'
                                        }`}
                                    >
                                        {applyingMod ? (
                                            <>
                                                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                                                {t('workout.applying')}
                                            </>
                                        ) : (
                                            <>
                                                <span>✨</span> {t('workout.apply_recommendations')}
                                            </>
                                        )}
                                    </button>

                                    <button
                                        onClick={handleDismissModifications}
                                        disabled={applyingMod || dismissingMod}
                                        className="w-full py-4 rounded-2xl font-bold text-gray-500 hover:bg-gray-100 border-2 border-gray-200 hover:border-gray-300 transition"
                                    >
                                        {t('workout.dismiss_recommendations')}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : null}

                    {/* Coach analysis rating */}
                    <div className="mt-10 border-t border-gray-200 pt-8">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-black text-gray-900 uppercase tracking-wide">
                          {t('workout.coach_rating_prompt')}
                        </div>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            disabled={!canEdit || coachRatingSaving}
                            onClick={async () => {
                              if (!canEdit || coachRatingSaving) return;
                              const next = coachRating === 'up' ? null : 'up';
                              setCoachRatingSaving(true);
                              try {
                                const res = await fetch(`${API_BASE}/workouts/${workoutId}/coach-rating`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  credentials: 'include',
                                  body: JSON.stringify({ rating: next })
                                });
                                const data = await res.json().catch(() => ({}));
                                if (!res.ok) throw new Error(data?.error || 'Failed');
                                setDebrief((prev) => ({ ...(prev || {}), coach_rating: next }));
                              } catch (e) {
                                alert(e?.message || 'Failed to save');
                              } finally {
                                setCoachRatingSaving(false);
                              }
                            }}
                            className={[
                              'p-3 rounded-2xl flex items-center justify-center cursor-pointer transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 border',
                              coachRating === 'up' ? 'bg-green-600 text-white border-green-600' : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300 hover:text-green-600',
                              (!canEdit || coachRatingSaving) ? 'opacity-60 cursor-not-allowed' : ''
                            ].join(' ')}
                          >
                            <FaThumbsUp className="w-5 h-5" />
                          </button>

                          <button
                            type="button"
                            disabled={!canEdit || coachRatingSaving}
                            onClick={async () => {
                              if (!canEdit || coachRatingSaving) return;
                              const next = coachRating === 'down' ? null : 'down';
                              setCoachRatingSaving(true);
                              try {
                                const res = await fetch(`${API_BASE}/workouts/${workoutId}/coach-rating`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  credentials: 'include',
                                  body: JSON.stringify({ rating: next })
                                });
                                const data = await res.json().catch(() => ({}));
                                if (!res.ok) throw new Error(data?.error || 'Failed');
                                setDebrief((prev) => ({ ...(prev || {}), coach_rating: next }));
                              } catch (e) {
                                alert(e?.message || 'Failed to save');
                              } finally {
                                setCoachRatingSaving(false);
                              }
                            }}
                            className={[
                              'p-3 rounded-2xl flex items-center justify-center cursor-pointer transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 border',
                              coachRating === 'down' ? 'bg-red-600 text-white border-red-600' : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300 hover:text-red-600',
                              (!canEdit || coachRatingSaving) ? 'opacity-60 cursor-not-allowed' : ''
                            ].join(' ')}
                          >
                            <FaThumbsDown className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="mt-10">
                         <StravaFormatCard
                          workoutId={workoutId}
                          canEdit={canEdit}
                          debrief={debrief}
                          formattedAt={workout?.strava_formatted_at || null}
                        />
                    </div>
                </div>
            ) : !canEdit ? (
                <div className="border-t pt-8 mt-8">
                    <h3 className="font-black text-xl mb-4 text-gray-900">{t('workout.complete_session')}</h3>
                    <div className="w-full py-4 rounded-2xl font-bold bg-gray-50 text-gray-500 text-center border border-gray-100">
                        {t('workout.read_only')}
                    </div>
                </div>
            ) : (
                <div className="border-t border-gray-100 pt-10 mt-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-1.5 h-8 bg-gray-400 rounded-full"></div>
                        <h3 className="font-black text-xl text-gray-900 tracking-tight">{t('workout.complete_session')}</h3>
                    </div>
                    
                    <div
                        className={`border-2 border-dashed rounded-3xl p-10 text-center cursor-pointer transition-all duration-300 group ${file ? 'border-green-500 bg-green-50/50' : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'}`}
                        onClick={() => document.getElementById('workout-gpx-upload').click()}
                    >
                        <div className="w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform text-indigo-500">
                             <HiCloudArrowUp className="w-8 h-8" />
                        </div>
                        <p className="font-bold text-gray-700 text-lg mb-1">{file ? file.name : t('workout.upload_gpx')}</p>
                        <p className="text-sm text-gray-400">.gpx files supported</p>
                        <input
                            id="workout-gpx-upload"
                            type="file"
                            accept=".gpx"
                            className="hidden"
                            onChange={(e) => setFile(e.target.files[0])}
                        />
                    </div>
                    <button
                        onClick={handleUpload}
                        disabled={!file || uploading}
                        className={`mt-6 w-full py-4 rounded-full font-black text-white text-lg shadow-xl transition-all ${!file || uploading ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-500/30 hover:-translate-y-0.5'}`}
                    >
                        {uploading ? (
                            <span className="flex items-center justify-center gap-2">
                                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                                {t('workout.analyzing')}
                            </span>
                        ) : t('workout.analyze_complete')}
                    </button>
                </div>
            )}

            <MobileBottomTabs
                value="calendar"
                onChange={handleMobileTabChange}
            />
        </div>
    );
};

export default WorkoutDetails;